import { GameState } from '../game/GameState';
import { Deck } from '../game/Deck';
import type { TooltipData } from '../types';
import { getResourceIcon, getStatIcon, replaceResourceNamesWithSymbols } from '../utils/Icons';

export class Renderer {
  private app: HTMLElement;
  private gameState: GameState;
  private deck: Deck;
  private tooltips: TooltipData;
  private helpTexts: any;
  private onSkipToRegrets: (() => void) | null = null;
  private onNewGame: (() => void) | null = null;
  
  constructor(app: HTMLElement, gameState: GameState, deck: Deck, tooltips: TooltipData, helpTexts: any = {}) {
    this.app = app;
    this.gameState = gameState;
    this.deck = deck;
    this.tooltips = tooltips;
    this.helpTexts = helpTexts;
  }
  
  setOnSkipToRegrets(handler: () => void): void {
    this.onSkipToRegrets = handler;
  }
  
  setOnNewGame(handler: () => void): void {
    this.onNewGame = handler;
  }
  
  private handleSkipToRegrets(): void {
    if (this.onSkipToRegrets) {
      this.onSkipToRegrets();
    }
  }
  
  private handleNewGame(): void {
    if (this.onNewGame) {
      this.onNewGame();
    }
  }
  
  render(): void {
    this.app.innerHTML = '';
    
    // Apply game-like styling
    this.app.style.cssText = `
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
      color: #e0e0e0;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      overflow: hidden;
      position: relative;
    `;
    
    // Render based on phase
    switch (this.gameState.phase) {
      case 'class_selection':
        this.renderClassSelection();
        break;
      case 'card_selection':
        this.renderCardSelection();
        break;
      case 'regrets':
        this.renderRegretsPhase();
        break;
      case 'game_over':
        this.renderGameOver();
        break;
    }
  }
  
  private renderClassSelection(): void {
    // This will be handled by ClassSelection component
  }
  
  private renderCardSelection(): void {
    const container = document.createElement('div');
    container.style.cssText = `
      display: grid;
      grid-template-rows: auto 1fr;
      height: 100%;
      padding: 0;
      gap: 0;
      overflow: hidden;
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%);
    `;
    
    // Top bar with resources and stats
    const topBar = this.createResourceBar();
    container.appendChild(topBar);
    
    // Main game area - use grid to fill space better
    const mainArea = document.createElement('div');
    mainArea.style.cssText = `
      display: grid;
      grid-template-rows: auto auto 1fr auto;
      gap: 20px;
      padding: 40px;
      overflow-y: auto;
      background: 
        radial-gradient(circle at 20% 50%, rgba(74, 159, 255, 0.05) 0%, transparent 50%),
        radial-gradient(circle at 80% 50%, rgba(255, 74, 74, 0.05) 0%, transparent 50%),
        linear-gradient(180deg, rgba(26, 26, 26, 0.3) 0%, rgba(15, 15, 15, 0.5) 100%);
    `;
    
    // Title section
    const titleSection = document.createElement('div');
    titleSection.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 15px;
    `;
    
    const title = document.createElement('h1');
    title.style.cssText = `
      font-size: clamp(28px, 3.5vw, 42px);
      color: #fff;
      text-shadow: 3px 3px 6px rgba(0,0,0,0.8), 0 0 20px rgba(74, 159, 255, 0.3);
      margin: 0;
      font-weight: 300;
      letter-spacing: clamp(1px, 0.2vw, 2px);
    `;
    title.textContent = 'Choose Your Path';
    titleSection.appendChild(title);
    
    // Deck info and Skip to Regrets button
    const deckInfoContainer = document.createElement('div');
    deckInfoContainer.style.cssText = `
      display: flex;
      gap: 15px;
      align-items: center;
    `;
    
    const deckInfo = document.createElement('div');
    deckInfo.style.cssText = `
      color: #aaa;
      font-size: clamp(14px, 1.6rem, 20px);
      padding: clamp(8px, 1.2vh, 12px) clamp(16px, 2vw, 24px);
      background: rgba(0, 0, 0, 0.4);
      border-radius: clamp(8px, 1vw, 12px);
      border: 1px solid rgba(255, 255, 255, 0.15);
      box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
    `;
    deckInfo.textContent = `Deck: ${this.deck.getDeckSize()} cards | Discard: ${this.deck.getDiscardSize()} cards`;
    deckInfoContainer.appendChild(deckInfo);
    
    // Skip to Regrets button (only show when 4 or fewer cards)
    if (this.deck.getDeckSize() <= 4) {
      const skipButton = document.createElement('button');
      skipButton.style.cssText = `
        padding: 12px 24px;
        background: linear-gradient(135deg, rgba(255, 74, 74, 0.8) 0%, rgba(200, 50, 50, 0.8) 100%);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 12px;
        color: #fff;
        font-size: clamp(12px, 1.4rem, 16px);
        font-weight: bold;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
      `;
      skipButton.textContent = 'Skip to Regrets';
      skipButton.setAttribute('data-tooltip', 'Discard all remaining cards instead of playing them');
      skipButton.addEventListener('mouseenter', () => {
        skipButton.style.background = 'linear-gradient(135deg, rgba(255, 100, 100, 0.9) 0%, rgba(220, 70, 70, 0.9) 100%)';
        skipButton.style.transform = 'scale(1.05)';
        skipButton.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.4)';
      });
      skipButton.addEventListener('mouseleave', () => {
        skipButton.style.background = 'linear-gradient(135deg, rgba(255, 74, 74, 0.8) 0%, rgba(200, 50, 50, 0.8) 100%)';
        skipButton.style.transform = 'scale(1)';
        skipButton.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
      });
      skipButton.addEventListener('click', () => {
        this.handleSkipToRegrets();
      });
      this.setupTooltip(skipButton, 'Skip directly to the regrets phase');
      deckInfoContainer.appendChild(skipButton);
    }
    
    titleSection.appendChild(deckInfoContainer);
    
    mainArea.appendChild(titleSection);
    
    // Cards will be rendered here by CardUI
    const cardsContainer = document.createElement('div');
    cardsContainer.id = 'cards-container';
    cardsContainer.style.cssText = `
      display: flex;
      justify-content: center;
      align-items: flex-start;
      min-height: 0;
    `;
    mainArea.appendChild(cardsContainer);
    
    // Description at the bottom
    const descriptionDiv = document.createElement('div');
    descriptionDiv.style.cssText = `
      width: 100%;
      max-width: 1400px;
      padding: clamp(20px, 2.5vh, 30px) clamp(25px, 3vw, 40px);
      text-align: center;
      margin: 0 auto;
    `;
    
    const descriptionText = document.createElement('p');
    descriptionText.style.cssText = `
      font-size: clamp(16px, 1.8rem, 20px);
      color: #aaa;
      line-height: 1.8;
      margin: 0;
      font-style: italic;
      max-width: min(900px, 70vw);
      margin: 0 auto;
    `;
    
    // Informative description about the game
    const generalDescriptionText = this.helpTexts.bottom_description || "You are struggling to rebuild your life. Make choices to increase your Dignity (reach 6 to win) while managing your resources and avoiding losing all your Sanity (0 means defeat). Each card presents opportunities, challenges, and habits that can help or hinder your progress.";
    const generalDescription = replaceResourceNamesWithSymbols(generalDescriptionText);
    
    descriptionText.innerHTML = generalDescription;
    descriptionDiv.appendChild(descriptionText);
    mainArea.appendChild(descriptionDiv);
    
    container.appendChild(mainArea);
    this.app.appendChild(container);
  }
  
  private createNewGameButton(): HTMLElement {
    const button = document.createElement('button');
    button.style.cssText = `
      padding: 15px 30px;
      background: linear-gradient(135deg, rgba(74, 159, 255, 0.8) 0%, rgba(42, 111, 204, 0.8) 100%);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      color: #fff;
      font-size: clamp(14px, 1.6rem, 18px);
      font-weight: bold;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    `;
    button.textContent = 'New Game';
    button.addEventListener('mouseenter', () => {
      button.style.background = 'linear-gradient(135deg, rgba(106, 176, 255, 0.9) 0%, rgba(60, 130, 220, 0.9) 100%)';
      button.style.transform = 'scale(1.05)';
      button.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.4)';
    });
    button.addEventListener('mouseleave', () => {
      button.style.background = 'linear-gradient(135deg, rgba(74, 159, 255, 0.8) 0%, rgba(42, 111, 204, 0.8) 100%)';
      button.style.transform = 'scale(1)';
      button.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
    });
    button.addEventListener('click', () => {
      this.handleNewGame();
    });
    return button;
  }
  
  private renderRegretsPhase(): void {
    // This will be handled by RegretsPhase component
  }
  
  private renderGameOver(): void {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      gap: 30px;
    `;
    
    const title = document.createElement('h1');
    title.style.cssText = `
      font-size: clamp(36px, 4.5vw, 48px);
      color: ${this.gameState.checkWinCondition() ? '#4a9eff' : '#ff4a4a'};
      text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
    `;
    title.textContent = this.gameState.checkWinCondition() ? 'Victory' : 'Defeat';
    
    const message = document.createElement('p');
    message.style.cssText = `
      font-size: clamp(18px, 2.2rem, 24px);
      color: #ccc;
      text-align: center;
      margin-bottom: 20px;
    `;
    message.textContent = this.gameState.checkWinCondition()
      ? 'You found your dignity and turned your life around.'
      : 'Your sanity has been lost. The regrets were too much to bear.';
    
    // Show final Sanity and Dignity values
    const finalStats = document.createElement('div');
    finalStats.style.cssText = `
      display: flex;
      gap: 30px;
      font-size: clamp(16px, 1.8rem, 20px);
      color: #aaa;
      margin-bottom: 20px;
    `;
    
    const sanityDisplay = document.createElement('div');
    sanityDisplay.style.cssText = 'display: flex; align-items: center; gap: 8px;';
    sanityDisplay.innerHTML = `Sanity: ${this.gameState.getResource('sanity')} ${getResourceIcon('sanity')}`;
    
    const dignityDisplay = document.createElement('div');
    dignityDisplay.style.cssText = 'display: flex; align-items: center; gap: 8px;';
    dignityDisplay.innerHTML = `Dignity: ${this.gameState.getResource('dignity')} ${getResourceIcon('dignity')}`;
    
    finalStats.appendChild(sanityDisplay);
    finalStats.appendChild(dignityDisplay);
    
    const newGameButton = this.createNewGameButton();
    
    container.appendChild(title);
    container.appendChild(message);
    container.appendChild(finalStats);
    container.appendChild(newGameButton);
    this.app.appendChild(container);
  }
  
  private createResourceBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.style.cssText = `
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      align-items: center;
      padding: clamp(12px, 1.5vh, 20px) clamp(20px, 3vw, 40px);
      background: 
        linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 100%),
        linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%);
      border-bottom: clamp(2px, 0.3vh, 3px) solid rgba(255, 255, 255, 0.15);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
      gap: clamp(15px, 2vw, 30px);
    `;
    
    // Regular resources
    const resources = document.createElement('div');
    resources.style.cssText = 'display: flex; gap: 25px; flex-wrap: wrap; justify-content: flex-start; align-items: center;';
    
    const regularResourceList = [
      { key: 'money', label: 'Money', symbol: '💰', value: this.gameState.getResource('money'), color: '#4a9eff' },
      { key: 'food', label: 'Food', symbol: '🍞', value: this.gameState.getResource('food'), color: '#ff9f4a' },
      { key: 'motivation', label: 'Motivation', symbol: '⚡', value: this.gameState.getResource('motivation'), color: '#9f4aff' },
      { key: 'memory', label: 'Memory', symbol: '💭', value: this.gameState.getResource('memory'), color: '#ff4a9f' },
      { key: 'connections', label: 'Connections', symbol: '🤝', value: this.gameState.getResource('connections'), color: '#4aff9f' }
    ];
    
    for (const res of regularResourceList) {
      const elem = this.createResourceDisplayWithSymbol(res.key, res.label, res.symbol, res.value, res.color);
      resources.appendChild(elem);
    }
    
    // Help button - right side of resources, just left of special resources
    const helpButton = document.createElement('button');
    helpButton.textContent = '?';
    helpButton.style.cssText = `
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: rgba(74, 159, 255, 0.2);
      border: 2px solid rgba(74, 159, 255, 0.5);
      color: #4a9eff;
      font-size: clamp(14px, 1.6rem, 18px);
      font-weight: bold;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      margin-left: auto;
    `;
    helpButton.addEventListener('mouseenter', () => {
      helpButton.style.background = 'rgba(74, 159, 255, 0.4)';
      helpButton.style.borderColor = 'rgba(74, 159, 255, 0.8)';
      helpButton.style.transform = 'scale(1.1)';
    });
    helpButton.addEventListener('mouseleave', () => {
      helpButton.style.background = 'rgba(74, 159, 255, 0.2)';
      helpButton.style.borderColor = 'rgba(74, 159, 255, 0.5)';
      helpButton.style.transform = 'scale(1)';
    });
    helpButton.addEventListener('click', () => {
      this.showHelpModal();
    });
    resources.appendChild(helpButton);
    
    // Special resources (separated) - center
    const specialResources = document.createElement('div');
    specialResources.style.cssText = `
      display: flex; 
      gap: 25px; 
      flex-wrap: wrap; 
      justify-content: center;
      border-left: 2px solid rgba(255, 255, 255, 0.2); 
      border-right: 2px solid rgba(255, 255, 255, 0.2);
      padding: 0 30px;
    `;
    
    const specialResourceList = [
      { key: 'health', label: 'Health', symbol: '❤️', value: this.gameState.getResource('health'), color: '#ff4a4a' },
      { key: 'bad_habit', label: 'Bad Habits', symbol: '⚠️', value: this.gameState.getResource('bad_habit'), color: '#ffaa00' },
      { key: 'sanity', label: 'Sanity', symbol: '🧠', value: this.gameState.getResource('sanity'), color: '#aa4aff' }
    ];
    
    for (const res of specialResourceList) {
      const elem = this.createResourceDisplayWithSymbol(res.key, res.label, res.symbol, res.value, res.color);
      specialResources.appendChild(elem);
    }
    
    // Dignity as progress bar
    const dignityElem = this.createDignityProgressBar();
    specialResources.appendChild(dignityElem);
    
    // Inspiration as progress bar
    const inspirationElem = this.createInspirationProgressBar();
    specialResources.appendChild(inspirationElem);
    
    // Stats - right side
    const stats = document.createElement('div');
    stats.style.cssText = 'display: flex; gap: 25px; flex-wrap: wrap; justify-content: flex-end; align-items: center;';
    
    // Add class pill if a class is selected
    const selectedClass = this.gameState.selectedClass;
    if (selectedClass) {
      const classPill = this.createClassPill(selectedClass.name);
      classPill.style.marginRight = '40px'; // More spacing between class pill and Smarts
      stats.appendChild(classPill);
    }
    
    const statList = [
      { key: 'smarts', label: 'Smarts', symbol: getStatIcon('smarts'), value: this.gameState.getStat('smarts') },
      { key: 'tenacity', label: 'Tenacity', symbol: '👁️', value: this.gameState.getStat('tenacity') },
      { key: 'social', label: 'Social', symbol: '💬', value: this.gameState.getStat('social') }
    ];
    
    for (const stat of statList) {
      const elem = this.createStatDisplay(stat.key, stat.label, stat.symbol, stat.value);
      stats.appendChild(elem);
    }
    
    // New Game button on the right side
    const newGameButton = this.createNewGameButton();
    // Override some styles for the header version
    newGameButton.style.padding = '10px 20px';
    newGameButton.style.fontSize = '14px';
    newGameButton.style.marginLeft = '20px';
    stats.appendChild(newGameButton);
    
    bar.appendChild(resources);
    bar.appendChild(specialResources);
    bar.appendChild(stats);
    return bar;
  }
  
  private createResourceDisplayWithSymbol(key: string, label: string, symbol: string, value: number, color: string): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      cursor: help;
      position: relative;
    `;
    
    const symbolElem = document.createElement('div');
    symbolElem.style.cssText = 'font-size: clamp(18px, 2.2rem, 24px);';
    symbolElem.textContent = symbol;
    
    const valueElem = document.createElement('div');
    valueElem.style.cssText = `
      font-size: clamp(16px, 1.8rem, 20px);
      font-weight: bold;
      color: ${color};
    `;
    valueElem.textContent = value.toString();
    
    const labelElem = document.createElement('div');
    labelElem.style.cssText = `
      font-size: 16px;
      color: #888;
    `;
    labelElem.textContent = label;
    
    // Tooltip
    const tooltip = this.tooltips[key];
    if (tooltip) {
      container.setAttribute('data-tooltip', tooltip);
      this.setupTooltip(container, tooltip);
    }
    
    container.appendChild(symbolElem);
    container.appendChild(valueElem);
    container.appendChild(labelElem);
    return container;
  }
  
  private createStatDisplay(key: string, label: string, symbol: string, value: number): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      cursor: help;
      position: relative;
    `;
    
    const symbolElem = document.createElement('div');
    symbolElem.style.cssText = 'font-size: clamp(18px, 2.2rem, 24px);';
    symbolElem.textContent = symbol;
    
    const valueElem = document.createElement('div');
    valueElem.style.cssText = `
      font-size: clamp(14px, 1.6rem, 18px);
      font-weight: bold;
      color: #fff;
    `;
    valueElem.textContent = (value ?? 0).toString();
    
    const labelElem = document.createElement('div');
    labelElem.style.cssText = `
      font-size: 16px;
      color: #888;
    `;
    labelElem.textContent = label;
    
    // Tooltip
    const tooltip = this.tooltips[key];
    if (tooltip) {
      container.setAttribute('data-tooltip', tooltip);
      this.setupTooltip(container, tooltip);
    }
    
    container.appendChild(symbolElem);
    container.appendChild(valueElem);
    container.appendChild(labelElem);
    return container;
  }
  
  private createClassPill(className: string): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      position: relative;
    `;
    
    const labelElem = document.createElement('div');
    labelElem.style.cssText = `
      font-size: 16px;
      color: #888;
    `;
    labelElem.textContent = 'Class';
    
    const pill = document.createElement('div');
    pill.style.cssText = `
      padding: 6px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 20px;
      font-size: 14px;
      color: #fff;
      white-space: nowrap;
      font-weight: 500;
    `;
    pill.textContent = className;
    
    // Tooltip with class description
    const selectedClass = this.gameState.selectedClass;
    if (selectedClass && selectedClass.description) {
      container.setAttribute('data-tooltip', selectedClass.description);
      this.setupTooltip(container, selectedClass.description);
    }
    
    container.appendChild(labelElem);
    container.appendChild(pill);
    return container;
  }
  
  private createDignityProgressBar(): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      cursor: help;
      position: relative;
      min-width: 100px;
    `;
    
    const symbolElem = document.createElement('div');
    symbolElem.style.cssText = 'font-size: clamp(18px, 2.2rem, 24px);';
    symbolElem.textContent = '⭐';
    
    const progressContainer = document.createElement('div');
    progressContainer.style.cssText = `
      width: 100px;
      height: 20px;
      background: rgba(0, 0, 0, 0.5);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      overflow: hidden;
      position: relative;
    `;
    
    const dignity = this.gameState.getResource('dignity');
    const maxDignity = 6;
    const percentage = Math.min(100, (dignity / maxDignity) * 100);
    
    const progressBar = document.createElement('div');
    progressBar.style.cssText = `
      width: ${percentage}%;
      height: 100%;
      background: linear-gradient(90deg, #4affff 0%, #2a9fcc 100%);
      transition: width 0.3s ease;
      box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.3);
    `;
    
    const progressText = document.createElement('div');
    progressText.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 16px;
      font-weight: bold;
      color: #fff;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
      z-index: 1;
    `;
    progressText.textContent = `${dignity}/${maxDignity}`;
    
    progressContainer.appendChild(progressBar);
    progressContainer.appendChild(progressText);
    
    const labelElem = document.createElement('div');
    labelElem.style.cssText = `
      font-size: 16px;
      color: #888;
    `;
    labelElem.textContent = 'Dignity';
    
    // Tooltip - disable browser default
    const tooltip = this.tooltips['dignity'];
    if (tooltip) {
      container.setAttribute('data-tooltip', tooltip);
      this.setupTooltip(container, tooltip);
    }
    
    container.appendChild(symbolElem);
    container.appendChild(progressContainer);
    container.appendChild(labelElem);
    return container;
  }
  
  private createInspirationProgressBar(): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      cursor: help;
      position: relative;
      min-width: 100px;
    `;
    
    const symbolElem = document.createElement('div');
    symbolElem.style.cssText = 'font-size: clamp(18px, 2.2rem, 24px);';
    symbolElem.textContent = '💡';
    
    const progressContainer = document.createElement('div');
    progressContainer.style.cssText = `
      width: 100px;
      height: 20px;
      background: rgba(0, 0, 0, 0.5);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      overflow: hidden;
      position: relative;
    `;
    
    const inspiration = this.gameState.getInspirationsUnlocked();
    const maxInspiration = 3;
    const percentage = Math.min(100, (inspiration / maxInspiration) * 100);
    
    const progressBar = document.createElement('div');
    progressBar.style.cssText = `
      width: ${percentage}%;
      height: 100%;
      background: linear-gradient(90deg, #9f4aff 0%, #7a2fcc 100%);
      transition: width 0.3s ease;
      box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.3);
    `;
    
    const progressText = document.createElement('div');
    progressText.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 16px;
      font-weight: bold;
      color: #fff;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
      z-index: 1;
    `;
    progressText.textContent = `${inspiration}/${maxInspiration}`;
    
    progressContainer.appendChild(progressBar);
    progressContainer.appendChild(progressText);
    
    const labelElem = document.createElement('div');
    labelElem.style.cssText = `
      font-size: 16px;
      color: #888;
    `;
    labelElem.textContent = 'Inspiration';
    
    // Tooltip - disable browser default
    const tooltip = 'Unlocks new thinking actions. Maximum 3 inspirations.';
    container.setAttribute('data-tooltip', tooltip);
    this.setupTooltip(container, tooltip);
    
    container.appendChild(symbolElem);
    container.appendChild(progressContainer);
    container.appendChild(labelElem);
    return container;
  }
  
  private cleanupTooltips(): void {
    // Remove all tooltips from the document
    document.querySelectorAll('.game-tooltip').forEach(tooltip => {
      if (tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    });
  }
  
  private setupTooltip(element: HTMLElement, text: string): void {
    // Enhanced tooltip with better styling
    let tooltipElement: HTMLElement | null = null;
    
    element.addEventListener('mouseenter', (_e) => {
      // Clean up any existing tooltips first
      this.cleanupTooltips();
      
      tooltipElement = document.createElement('div');
      tooltipElement.className = 'game-tooltip';
      tooltipElement.style.cssText = `
        position: absolute;
        background: rgba(0, 0, 0, 0.95);
        color: #fff;
        padding: 10px 15px;
        border-radius: 8px;
        font-size: 17px;
        line-height: 1.5;
        max-width: 250px;
        z-index: 10000;
        pointer-events: none;
        border: 1px solid rgba(255, 255, 255, 0.2);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
        white-space: normal;
        word-wrap: break-word;
      `;
      tooltipElement.textContent = text;
      document.body.appendChild(tooltipElement);
      
      const rect = element.getBoundingClientRect();
      tooltipElement.style.left = `${rect.left + rect.width / 2 - tooltipElement.offsetWidth / 2}px`;
      tooltipElement.style.top = `${rect.bottom + 10}px`;
      
      // Adjust if tooltip goes off screen
      const tooltipRect = tooltipElement.getBoundingClientRect();
      if (tooltipRect.right > window.innerWidth) {
        tooltipElement.style.left = `${window.innerWidth - tooltipElement.offsetWidth - 10}px`;
      }
      if (tooltipRect.left < 0) {
        tooltipElement.style.left = '10px';
      }
      if (tooltipRect.bottom > window.innerHeight) {
        tooltipElement.style.top = `${rect.top - tooltipElement.offsetHeight - 10}px`;
      }
    });
    
    element.addEventListener('mouseleave', () => {
      if (tooltipElement && tooltipElement.parentNode) {
        tooltipElement.parentNode.removeChild(tooltipElement);
        tooltipElement = null;
      }
    });
  }
  
  private showHelpModal(): void {
    // Clean up any tooltips before showing modal
    this.cleanupTooltips();
    
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.24s ease;
    `;
    
    // Create modal content
    const modal = document.createElement('div');
    modal.style.cssText = `
      background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 16px;
      padding: 30px;
      max-width: 700px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      animation: slideUp 0.24s ease;
    `;
    
    // Title
    const title = document.createElement('h2');
    title.style.cssText = `
      font-size: 28px;
      color: #fff;
      margin-bottom: 20px;
      border-bottom: 2px solid rgba(255, 255, 255, 0.1);
      padding-bottom: 15px;
    `;
    title.textContent = this.helpTexts.title || 'How to Play';
    modal.appendChild(title);
    
    // Content
    const content = document.createElement('div');
    content.style.cssText = `
      font-size: clamp(14px, 1.6rem, 18px);
      color: #ccc;
      line-height: 1.8;
    `;
    
    // Load sections from YAML or use defaults
    const sections = this.helpTexts.sections || [
      {
        heading: 'Objective',
        text: 'You are struggling to rebuild your life. Your goal is to reach 6 Dignity to win the game. Be careful: if your Sanity reaches 0, you lose.'
      },
      {
        heading: 'Card Selection Phase',
        text: 'Each turn, you see 4 cards from your deck. Click a card to see its options. The first option is always free and available. Other options may require resources (Money, Food, etc.) or stats (Smarts, Tenacity, Social). You can pay resource costs with Sanity if you don\'t have enough. Time costs require discarding cards from your deck.'
      },
      {
        heading: 'Resources & Stats',
        text: 'Resources (Money, Food, Motivation, Memory, Connections) are used to pay for options. Health, Bad Habits, and Sanity track your wellbeing. Dignity (0/6) is your progress toward victory. Inspiration (0/3) unlocks new thinking actions. Skills (Smarts, Tenacity, Social) are required for some options.'
      },
      {
        heading: 'Regrets Phase',
        text: 'After your deck runs out, you enter the Regrets Phase. This phase has 4 steps: (1) Lose Health based on your Bad Habits, (2) Pay bills (9 Money or lose Sanity), (3) Cover basic needs (choose what to sacrifice), and (4) Maintain social connections (spend Connections or lose Sanity). After completing the Regrets Phase, your deck is reshuffled and you continue playing.'
      },
      {
        heading: 'Exhausting Cards',
        text: 'Some options permanently remove cards from the game (exhaust them). This usually happens when you take high-value opportunities or overcome bad habits. Exhausted cards cannot be drawn again.'
      }
    ];
    
    sections.forEach((section: any, index: number) => {
      const sectionDiv = document.createElement('div');
      sectionDiv.style.cssText = `margin-bottom: ${index < sections.length - 1 ? '25px' : '0'};`;
      
      const heading = document.createElement('h3');
      heading.style.cssText = `
        font-size: 22px;
        color: #4a9eff;
        margin-bottom: 10px;
        font-weight: bold;
      `;
      heading.textContent = section.heading;
      sectionDiv.appendChild(heading);
      
      // Handle sections with list items
      if (section.list_items && Array.isArray(section.list_items)) {
        // Text before list (if any)
        if (section.text) {
          const textBefore = document.createElement('p');
          textBefore.style.cssText = `
            margin: 0 0 10px 0;
            color: #ccc;
          `;
          const textWithSymbols = replaceResourceNamesWithSymbols(section.text);
          textBefore.innerHTML = textWithSymbols;
          sectionDiv.appendChild(textBefore);
        }
        
        // Create bullet list
        const list = document.createElement('ul');
        list.style.cssText = `
          margin: 0 0 10px 0;
          padding-left: 25px;
          color: #ccc;
        `;
        
        section.list_items.forEach((item: string) => {
          const listItem = document.createElement('li');
          listItem.style.cssText = `
            margin-bottom: 8px;
            line-height: 1.6;
          `;
          const itemWithSymbols = replaceResourceNamesWithSymbols(item);
          listItem.innerHTML = itemWithSymbols;
          list.appendChild(listItem);
        });
        
        sectionDiv.appendChild(list);
        
        // Text after list (if any)
        if (section.text_after) {
          const textAfter = document.createElement('p');
          textAfter.style.cssText = `
            margin: 10px 0 0 0;
            color: #ccc;
          `;
          const textWithSymbols = replaceResourceNamesWithSymbols(section.text_after);
          textAfter.innerHTML = textWithSymbols;
          sectionDiv.appendChild(textAfter);
        }
      } else {
        // Regular text section
        const text = document.createElement('p');
        text.style.cssText = `
          margin: 0;
          color: #ccc;
        `;
        // Replace resource and stat names with symbols
        const textWithSymbols = replaceResourceNamesWithSymbols(section.text);
        text.innerHTML = textWithSymbols;
        sectionDiv.appendChild(text);
      }
      
      content.appendChild(sectionDiv);
    });
    
    modal.appendChild(content);
    
    // Close button
    const closeButton = document.createElement('button');
    closeButton.style.cssText = `
      margin-top: 25px;
      padding: 12px 30px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: #fff;
      cursor: pointer;
      font-size: 16px;
      transition: all 0.2s ease;
      width: 100%;
    `;
    closeButton.textContent = this.helpTexts.close_button || 'Close';
    closeButton.addEventListener('mouseenter', () => {
      closeButton.style.background = 'rgba(255, 255, 255, 0.2)';
    });
    closeButton.addEventListener('mouseleave', () => {
      closeButton.style.background = 'rgba(255, 255, 255, 0.1)';
    });
    closeButton.addEventListener('click', () => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });
    modal.appendChild(closeButton);
    
    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      }
    });
    
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  }
}

